import { FC, createContext, useContext, useState, useCallback } from 'react';

import { DataPaginateResponse } from '~globals/types/api';
import { DashboardGiverStatitics } from '~services/dashboard/types';
import { getDashboardGiverStatitics } from '~services/dashboard';
import { AdminIntegration } from '~services/admin/integration/types';
import { getAdminIntegrations } from '~services/admin/integration';
import {
  useParseLocationQuery,
  useImportsOrdersDialogsState,
  useToggle,
  useRequest,
} from '~hooks/index';

import {
  DashboardVariantV2GiverQueryParams,
  DashboardVariantV2GiverContextValue,
  DashboardVariantV2GiverContextTabId,
} from './types';

const DEFAULT_STATISTICS: DashboardGiverStatitics = {
  totalItems: 0,
  inPreparationItems: 0,
  inTransitItems: 0,
  completed: {
    totalCompletedtems: 0,
    totalWithIssuesItems: 0,
    totalWithoutIssuesItems: 0,
  },
  carrierCompany: null,
};

const DEFAULT_INTEGRATIONS: AdminIntegration[] = [];

const DashboardVariantV2GiverContext =
  createContext<DashboardVariantV2GiverContextValue>({
    activeTab: {
      currentTab: 'inPreparation',
      setActiveTab: () => {},
      getIsActiveTab: () => false,
    },
    statistics: {
      loading: true,
      data: DEFAULT_STATISTICS,
      reloadData: () => {},
    },
    integrations: {
      loading: true,
      data: DEFAULT_INTEGRATIONS,
      reloadData: () => {},
    },
    actions: {
      ignoreRefreshBySocket: {
        value: false,
        toggleValue: () => {},
      },
      expandedHasChangeOrdersAlert: {
        value: false,
        toggleValue: () => {},
      },
      modifiedItemsIdBySocket: {
        value: [],
        setValue: () => {},
      },
    },
    dialogs: {
      importFromFile: { open: false, toggleOpen: () => {} },
      importFromIntegration: { open: false, toggleOpen: () => {} },
      updateGeoError: { open: false, toggleOpen: () => {} },
      integrations: { open: false, toggleOpen: () => {} },
      reports: { open: false, toggleOpen: () => {} },
      userSatisfaction: { open: false, toggleOpen: () => {} },
      heatmap: { open: false, toggleOpen: () => {} },
      configAndPreferences: { open: false, toggleOpen: () => {} },
    },
  });

export const useDashboardVariantV2GiverContext =
  (): DashboardVariantV2GiverContextValue =>
    useContext(DashboardVariantV2GiverContext);

const DashboardVariantV2GiverContextProvider: FC = ({ children }) => {
  const queryParams =
    useParseLocationQuery<DashboardVariantV2GiverQueryParams>();

  const [activeTab, setActiveTab] =
    useState<DashboardVariantV2GiverContextTabId>('inPreparation');
  const getIsActiveTab = useCallback(
    (currentTab: DashboardVariantV2GiverContextTabId) =>
      activeTab === currentTab,
    [activeTab],
  );

  const [statistics, setStatistics] =
    useState<DashboardGiverStatitics>(DEFAULT_STATISTICS);
  const [loadingStatistics, setLoadingStatistics] = useState(true);
  const [, , , reloadStatisticsRequest] = useRequest(
    {
      request: getDashboardGiverStatitics,
      payload: null,
      withPostSuccess: (response) => {
        const currentStatistics = response.data
          ?.data as DashboardGiverStatitics;

        setStatistics(currentStatistics);
      },
      withPostFetch: () => {
        setLoadingStatistics(false);
      },
    },
    [],
  );
  const reloadStatistics = useCallback(() => {
    reloadStatisticsRequest(null);
  }, [reloadStatisticsRequest]);

  const [integrations, setIntegrations] =
    useState<AdminIntegration[]>(DEFAULT_INTEGRATIONS);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const [, , , reloadIntegrationsRequest] = useRequest({
    request: getAdminIntegrations,
    payload: null,
    withPostSuccess: (response) => {
      const integrationsDataResponse = response.data as DataPaginateResponse<
        AdminIntegration[]
      >;

      setIntegrations(integrationsDataResponse.data.results);
    },
    withPostFetch: () => {
      setLoadingIntegrations(false);
    },
  });
  const reloadIntegrations = useCallback(() => {
    reloadIntegrationsRequest(null);
  }, [reloadIntegrationsRequest]);

  const [ignoreRefreshBySocket, toggleIgnoreRefreshBySocket] = useToggle(false);
  const [expandedHasChangeOrdersAlert, toggleExpandedHasChangeOrdersAlert] =
    useToggle(false);
  const [modifiedItemsIdBySocket, setModifiedItemsIdBySocket] = useState<
    string[]
  >([]);

  const {
    importFromFile: dialogImportFromFileToggleState,
    importFromIntegration: dialogImportFromIntegrationToggleState,
  } = useImportsOrdersDialogsState({
    defaultOpenDialogImportFromFile: queryParams?.openImporterFromFile,
    defaultOpenDialogImportFromIntegration:
      queryParams?.openImporterFromIntegration,
  });
  const [openDialogUpdateGeoError, toogleOpenDialogUpdateGeoError] =
    useToggle(false);
  const [openDialogIntegrations, toogleOpenDialogIntegrations] =
    useToggle(false);
  const [openDialogReports, toogleOpenDialogReports] = useToggle(false);
  const [openDialogUserSatisfaction, toogleOpenDialogUserSatisfaction] =
    useToggle(false);
  const [openDialogHeatmap, toogleOpenDialogHeatmap] = useToggle(false);
  const [openDialogConfigAndPreferences, toogleOpenDialogConfigAndPreferences] =
    useToggle(false);

  return (
    <DashboardVariantV2GiverContext.Provider
      value={{
        activeTab: {
          currentTab: activeTab,
          setActiveTab,
          getIsActiveTab,
        },
        statistics: {
          loading: loadingStatistics,
          data: statistics,
          reloadData: reloadStatistics,
        },
        integrations: {
          loading: loadingIntegrations,
          data: integrations,
          reloadData: reloadIntegrations,
        },
        actions: {
          ignoreRefreshBySocket: {
            value: ignoreRefreshBySocket,
            toggleValue: toggleIgnoreRefreshBySocket,
          },
          expandedHasChangeOrdersAlert: {
            value: expandedHasChangeOrdersAlert,
            toggleValue: toggleExpandedHasChangeOrdersAlert,
          },
          modifiedItemsIdBySocket: {
            value: modifiedItemsIdBySocket,
            setValue: setModifiedItemsIdBySocket,
          },
        },
        dialogs: {
          importFromFile: dialogImportFromFileToggleState,
          importFromIntegration: dialogImportFromIntegrationToggleState,
          updateGeoError: {
            open: openDialogUpdateGeoError,
            toggleOpen: toogleOpenDialogUpdateGeoError,
          },
          integrations: {
            open: openDialogIntegrations,
            toggleOpen: toogleOpenDialogIntegrations,
          },
          reports: {
            open: openDialogReports,
            toggleOpen: toogleOpenDialogReports,
          },
          userSatisfaction: {
            open: openDialogUserSatisfaction,
            toggleOpen: toogleOpenDialogUserSatisfaction,
          },
          heatmap: {
            open: openDialogHeatmap,
            toggleOpen: toogleOpenDialogHeatmap,
          },
          configAndPreferences: {
            open: openDialogConfigAndPreferences,
            toggleOpen: toogleOpenDialogConfigAndPreferences,
          },
        },
      }}
    >
      {children}
    </DashboardVariantV2GiverContext.Provider>
  );
};

export default DashboardVariantV2GiverContextProvider;
